import { z } from 'zod'

export const cableColorType = z.enum([
  'Standard',
  'Armored',
  'White',
  'Red',
  'Wires',
  'No Cable',
])

export type CableColorType = z.infer<typeof cableColorType>

// Cable Conduit Type - matches Joi validation (nullable)
export const cableConduitType = z
  .enum(['none', 'flex', 'pex', 'pexSand', 'hdpe', 'stainless'])
  .nullable()
  .default('none')

export type CableConduitType = z.infer<typeof cableConduitType>

// Cable Lead Component Type - matches Joi regex
export const cableLeadComponentType = z.enum([
  'no mlink',
  'mlink',
  'bare',
  'xlr',
  'raymo',
  'subconn',
  'none',
])

export type CableLeadComponentType = z.infer<typeof cableLeadComponentType>

// Body Component Type - matches Joi regex
export const cableBodyComponentType = z
  .string()
  .regex(
    /^(?:Sensor \d+|Protection\s\/\sSensor 1|zero marker|none|blank|D Ring)$/,
    'Body component must be "Sensor N", "Protection / Sensor 1", "zero marker", "blank", "D Ring", or "none"',
  )

export type CableBodyComponentType = z.infer<typeof cableBodyComponentType>

// Mold Color - matches Joi validation
export const moldColor = z.enum(['blue', 'white', 'black', 'none'])

export type MoldColorType = z.infer<typeof moldColor>

// Mold Type - matches Joi validation ('90degree' not '90_DEGREE')
// 'straight' is deprecated but kept for backward compatibility with existing cables
export const moldType = z.enum([
  'straight',
  'none',
  '90degree',
  'end',
  '11mm',
  '14mm',
  '9mm',
  '25mm',
  'd_ring',
])

export type MoldType = z.infer<typeof moldType>

// Mold object
export const moldSchema = z.object({
  type: moldType,
  color: moldColor,
})

export type Mold = z.infer<typeof moldSchema>

// Lead Section
export const leadSectionSchema = z.object({
  type: z.literal('lead'),
  spacingToNextSensorMm: z.number().int(), // was length
  positionFromSectionStartMm: z.number().int(), // was position
  cableColor: cableColorType,
  conduit: cableConduitType,
  component: cableLeadComponentType,
})

export type LeadSection = z.infer<typeof leadSectionSchema>

export const leadColumnSchema = z.array(leadSectionSchema).optional()

export type LeadColumn = z.infer<typeof leadColumnSchema>

// Body Section
export const bodySectionSchema = z.object({
  type: z.literal('body'),
  component: cableBodyComponentType,
  positionFromSectionStartMm: z.number().int(), // was position
  spacingToNextSensorMm: z.number().int(), // was length
  sensorNumber: z.number().int().optional(), // new field, corresponds to sensor number. might be undefiend. parsed from component string when possible
  order: z.number().int(),
  mold: moldSchema,
  cableColor: cableColorType,
  conduit: cableConduitType,
})

export type BodySection = z.infer<typeof bodySectionSchema>

// Body Column with custom validation for cable end
export const bodyColumnSchema = z
  .array(bodySectionSchema)
  .refine(
    (sections) => {
      // Check that no section except the last has mold type 'end'
      return sections.every(
        (section, index) =>
          index === sections.length - 1 || section.mold.type !== 'end',
      )
    },
    {
      message: 'there should not be an end in the middle of the cable',
    },
  )
  .refine((sections) => sections.length > 0, {
    message: 'the cable body must have at least one section',
  })
  .optional()

export type BodyColumn = z.infer<typeof bodyColumnSchema>

// Eeprom Pair
export const eepromPairSchema = z.union([
  z.object({
    id: z
      .string()
      .length(16)
      .toUpperCase()
      .regex(/^[0-9A-F]+$/, 'Must be uppercase hexadecimal'),
    pairedSensorId: z
      .string()
      .length(16)
      .toUpperCase()
      .regex(/^[0-9A-F]+$/, 'Must be uppercase hexadecimal'),
  }),
  z.object({
    id: z
      .string()
      .length(16)
      .toUpperCase()
      .regex(/^[0-9A-F]+$/, 'Must be uppercase hexadecimal'),
    pairedSensorId: z.literal('NONE'),
  }),
])

export type EepromPair = z.infer<typeof eepromPairSchema>

export const eepromColumnSchema = z
  .array(eepromPairSchema)
  .nullable()
  .optional()

export type EepromColumn = z.infer<typeof eepromColumnSchema>
